import React, { useMemo, useState } from "react";
import VolunteerList from "../../components/VolunteerList";
import ShiftTable from "../../components/shiftTable/ShiftTable";
import GuestRegistration from "../../components/guestRegistration/GuestRegistration";
import GuestList from "../../components/guestList/GuestList";
import EditVolunteerModal from "../../components/EditVolunteerModal";
import EditGuestModal from "../../components/guestList/EditGuestModal";
import DeleteGuestModal from "../../components/guestList/DeleteGuestModal";
import "./adminMobile.css";

// ============================================================
// ROLLEN
// ============================================================

const ROLE_ID_ADMIN = 1;
const ROLE_ID_OK = 2;
const ROLE_ID_SUPERHELFER = 3;
const ROLE_ID_HELFER = 4;

const ROLE_NAME_ADMIN = "ADMIN";
const ROLE_NAME_OK = "OK";
const ROLE_NAME_SUPERHELFER = "SUPERHELFER";
const ROLE_NAME_HELFER = "HELFER";

// ============================================================
// ADMIN-SCHICHTGRUPPEN
// ============================================================

const ADMIN_SHIFT_GROUPS = [
	{
		key: "chai-kuchen-kaffee",
		label: "Chai: Kuchen & Kaffee",
		departmentName: "Chai",
		department: "Chai",
		minShiftId: 301,
		maxShiftId: 307,
		order: 300
	},
	{
		key: "chai-crepes",
		label: "Chai: Crêpes",
		departmentName: "Chai",
		department: "Chai",
		minShiftId: 311,
		maxShiftId: 317,
		order: 310
	},
	{
		key: "food-festival",
		label: "Food: Festival",
		departmentName: "Food",
		department: "Food",
		minShiftId: 401,
		maxShiftId: 419,
		order: 400
	},
	{
		key: "food-zmorge",
		label: "Food: Zmorge",
		departmentName: "Food",
		department: "Food",
		minShiftId: 420,
		maxShiftId: 429,
		order: 420
	},
	{
		key: "food-aufbau",
		label: "Food: Aufbau",
		departmentName: "Food",
		department: "Food",
		minShiftId: 430,
		maxShiftId: 459,
		order: 430
	},
	{
		key: "food-abbau",
		label: "Food: Abbau",
		departmentName: "Food",
		department: "Food",
		minShiftId: 460,
		maxShiftId: 479,
		order: 460
	}
];

// ============================================================
// ALLGEMEINE HELPER
// ============================================================

const normalizeText = (value) => {
	return String(value || "").trim();
};

const normalizeUpper = (value) => {
	return normalizeText(value).toUpperCase();
};

const toNumberOrNull = (value) => {
	if (value === null || value === undefined || value === "") {
		return null;
	}

	const numberValue = Number(value);

	return Number.isNaN(numberValue) ? null : numberValue;
};

// ============================================================
// ROLLEN-HELPER
// ============================================================

const getVolunteerRoleId = (volunteer) => {
	if (!volunteer) return null;

	return (
		toNumberOrNull(volunteer.roleId) ??
		toNumberOrNull(volunteer.role_id) ??
		toNumberOrNull(volunteer.role?.roleId) ??
		toNumberOrNull(volunteer.role?.role_id) ??
		toNumberOrNull(volunteer.role?.id) ??
		null
	);
};

const getVolunteerRoleName = (volunteer) => {
	if (!volunteer) return "";

	const rawRole =
		volunteer.roleName ||
		volunteer.role_name ||
		volunteer.role?.name ||
		volunteer.role?.roleName ||
		volunteer.role?.authority ||
		(typeof volunteer.role === "string" ? volunteer.role : "");

	return normalizeUpper(rawRole).replace("ROLE_", "");
};

const isHiddenAdminUser = (volunteer) => {
	const firstName = normalizeUpper(
		volunteer?.firstName ||
		volunteer?.vorname ||
		volunteer?.firstname
	);

	const lastName = normalizeUpper(
		volunteer?.lastName ||
		volunteer?.name ||
		volunteer?.lastname
	);

	return firstName === "ADMIN" && lastName === "ADMIN";
};

const isAdminVolunteer = (volunteer) => {
	const roleId = getVolunteerRoleId(volunteer);
	const roleName = getVolunteerRoleName(volunteer);

	return roleId === ROLE_ID_ADMIN || roleName === ROLE_NAME_ADMIN;
};

const isVisibleShiftVolunteer = (volunteer) => {
	if (!volunteer) return false;
	if (isHiddenAdminUser(volunteer)) return false;
	if (isAdminVolunteer(volunteer)) return false;

	const roleId = getVolunteerRoleId(volunteer);
	const roleName = getVolunteerRoleName(volunteer);

	if ([ROLE_ID_OK, ROLE_ID_HELFER, ROLE_ID_SUPERHELFER].includes(roleId)) {
		return true;
	}

	return [ROLE_NAME_OK, ROLE_NAME_HELFER, ROLE_NAME_SUPERHELFER].includes(roleName);
};

// ============================================================
// NAMEN-HELPER
// ============================================================

const getPersonFullName = (entry) => {
	if (!entry) return "";

	if (typeof entry === "string") {
		return normalizeText(entry);
	}

	const directFullName =
		entry.fullName ||
		entry.full_name ||
		entry.displayName ||
		entry.display_name ||
		entry.username;

	if (directFullName) {
		return normalizeText(directFullName);
	}

	const firstName = normalizeText(
		entry.firstName ||
		entry.vorname ||
		entry.firstname ||
		entry.volunteer?.firstName ||
		entry.volunteer?.vorname ||
		entry.volunteer?.firstname ||
		entry.volunteerDTO?.firstName ||
		entry.volunteerDTO?.vorname ||
		entry.volunteerDTO?.firstname ||
		entry.person?.firstName ||
		entry.person?.vorname ||
		entry.person?.firstname
	);

	const lastName = normalizeText(
		entry.lastName ||
		entry.name ||
		entry.lastname ||
		entry.volunteer?.lastName ||
		entry.volunteer?.name ||
		entry.volunteer?.lastname ||
		entry.volunteerDTO?.lastName ||
		entry.volunteerDTO?.name ||
		entry.volunteerDTO?.lastname ||
		entry.person?.lastName ||
		entry.person?.name ||
		entry.person?.lastname
	);

	return `${firstName} ${lastName}`.trim();
};

const normalizeNames = (entries) => {
	if (!Array.isArray(entries)) {
		return [];
	}

	return entries
		.map(getPersonFullName)
		.filter(Boolean);
};

const addUniqueNames = (target, names) => {
	names.forEach((name) => {
		const cleanName = normalizeText(name);

		if (cleanName && !target.includes(cleanName)) {
			target.push(cleanName);
		}
	});
};

// ============================================================
// SHIFT-HELPER
// ============================================================

const getShiftId = (shift) => {
	return (
		toNumberOrNull(shift?.shiftId) ??
		toNumberOrNull(shift?.shift_id) ??
		toNumberOrNull(shift?.id) ??
		null
	);
};

const getDeploymentShiftId = (deployment) => {
	return (
		toNumberOrNull(deployment?.shiftId) ??
		toNumberOrNull(deployment?.shift_id) ??
		toNumberOrNull(deployment?.shift?.shiftId) ??
		toNumberOrNull(deployment?.shift?.shift_id) ??
		toNumberOrNull(deployment?.shift?.id) ??
		null
	);
};

const getDepartmentName = (shift) => {
	if (!shift) return "Unbekannt";

	if (typeof shift.departmentName === "string" && shift.departmentName.trim()) {
		return shift.departmentName.trim();
	}

	if (typeof shift.department === "string" && shift.department.trim()) {
		return shift.department.trim();
	}

	if (
		shift.departmentDto &&
		typeof shift.departmentDto.department === "string" &&
		shift.departmentDto.department.trim()
	) {
		return shift.departmentDto.department.trim();
	}

	if (
		shift.departmentDto &&
		typeof shift.departmentDto.name === "string" &&
		shift.departmentDto.name.trim()
	) {
		return shift.departmentDto.name.trim();
	}

	if (
		shift.department &&
		typeof shift.department.department === "string" &&
		shift.department.department.trim()
	) {
		return shift.department.department.trim();
	}

	return "Unbekannt";
};

const isShiftIdInRange = (shift, minShiftId, maxShiftId) => {
	const shiftId = getShiftId(shift);

	if (shiftId == null) {
		return false;
	}

	return shiftId >= minShiftId && shiftId <= maxShiftId;
};

const getAdminShiftGroupForShift = (shift) => {
	const departmentName = getDepartmentName(shift);

	const matchingGroup = ADMIN_SHIFT_GROUPS.find((group) => {
		return (
			group.department === departmentName &&
			isShiftIdInRange(shift, group.minShiftId, group.maxShiftId)
		);
	});

	if (matchingGroup) {
		return matchingGroup;
	}

	return {
		key: departmentName,
		label: departmentName,
		departmentName,
		department: departmentName,
		order: 1000
	};
};

// ============================================================
// DEPLOYMENT-HELPER
// ============================================================

const getVolunteerDeployments = (volunteer) => {
	const possibleLists = [
		volunteer?.volunteerDeployments,
		volunteer?.volunteer_deployments,
		volunteer?.volunteerDeployment,
		volunteer?.deployments,
		volunteer?.assignedShifts,
		volunteer?.assigned_shifts,
		volunteer?.shifts,
		volunteer?.shiftDtos
	];

	return possibleLists.find(Array.isArray) || [];
};

const getShiftDeploymentLists = (shift) => {
	return [
		shift?.volunteerDeployments,
		shift?.volunteer_deployments,
		shift?.deployments,
		shift?.assignments,
		shift?.assignedVolunteers,
		shift?.assignedVolunteerDtos,
		shift?.volunteers,
		shift?.libellen
	].filter(Array.isArray);
};

const collectNamesFromShiftObject = (shift) => {
	const names = [];

	addUniqueNames(names, normalizeNames(shift?.volunteerNames));
	addUniqueNames(names, normalizeNames(shift?.assignedVolunteerNames));
	addUniqueNames(names, normalizeNames(shift?.assignedVolunteers));
	addUniqueNames(names, normalizeNames(shift?.volunteers));
	addUniqueNames(names, normalizeNames(shift?.libellen));

	getShiftDeploymentLists(shift).forEach((list) => {
		addUniqueNames(names, normalizeNames(list));

		list.forEach((entry) => {
			addUniqueNames(names, [
				getPersonFullName(entry?.volunteer),
				getPersonFullName(entry?.volunteerDTO),
				getPersonFullName(entry?.person)
			]);
		});
	});

	return names;
};

const buildShiftIdToVolunteerNamesMap = (volunteers) => {
	const shiftIdToNames = new Map();

	(volunteers || []).forEach((volunteer) => {
		if (!isVisibleShiftVolunteer(volunteer)) {
			return;
		}

		const fullName = getPersonFullName(volunteer);

		if (!fullName) {
			return;
		}

		getVolunteerDeployments(volunteer).forEach((deployment) => {
			const shiftId = getDeploymentShiftId(deployment);

			if (shiftId == null) {
				return;
			}

			if (!shiftIdToNames.has(shiftId)) {
				shiftIdToNames.set(shiftId, []);
			}

			addUniqueNames(shiftIdToNames.get(shiftId), [fullName]);
		});
	});

	return shiftIdToNames;
};

const enrichShiftsWithVolunteerNames = (shifts, volunteers) => {
	const shiftIdToNames = buildShiftIdToVolunteerNamesMap(volunteers);

	return (shifts || []).map((shift) => {
		const shiftId = getShiftId(shift);
		const names = [];

		addUniqueNames(names, collectNamesFromShiftObject(shift));
		addUniqueNames(names, shiftIdToNames.get(shiftId) || []);

		return {
			...shift,
			volunteerNames: names,
			libellen: names,
			assignedVolunteerNames: names,
			assignedVolunteers: names,
			volunteers: names
		};
	});
};

// ============================================================
// ADMIN-SCHICHT-SEKTIONEN
// ============================================================

const buildAdminShiftSections = (shifts) => {
	const groups = new Map();

	(shifts || []).forEach((shift) => {
		const group = getAdminShiftGroupForShift(shift);

		if (!groups.has(group.key)) {
			groups.set(group.key, {
				key: group.key,
				label: group.label,
				departmentName: group.departmentName,
				order: group.order,
				shifts: []
			});
		}

		groups.get(group.key).shifts.push(shift);
	});

	return Array.from(groups.values())
		.map((section) => ({
			...section,
			shifts: section.shifts.sort((a, b) => {
				const shiftIdA = getShiftId(a) ?? 0;
				const shiftIdB = getShiftId(b) ?? 0;
				return shiftIdA - shiftIdB;
			})
		}))
		.sort((a, b) => {
			if (a.order !== b.order) {
				return a.order - b.order;
			}

			return a.label.localeCompare(b.label, "de", { sensitivity: "base" });
		});
};

// ============================================================
// COMPONENT
// ============================================================

const AdminMobile = ({
	volunteers,
	setVolunteers,
	shifts,
	guests,
	activeTab,
	setActiveTab,
	reloadGuests,
	reloadVolunteers,
	setSelectedVolunteer,
	selectedVolunteer
}) => {
	const [guestToEdit, setGuestToEdit] = useState(null);
	const [guestToDelete, setGuestToDelete] = useState(null);

	const tabs = [
		{ id: "overview", label: "Übersicht" },
		{ id: "helpers", label: "Volunteers" },
		{ id: "shifts", label: "Schichtplan" },
		{ id: "guests", label: "Gästeliste" }
	];

	const visibleVolunteers = useMemo(() => {
		return (volunteers || []).filter((volunteer) => {
			if (isHiddenAdminUser(volunteer)) return false;
			if (isAdminVolunteer(volunteer)) return false;

			return true;
		});
	}, [volunteers]);

	const helperVolunteers = useMemo(() => {
		return visibleVolunteers.filter((volunteer) => {
			const roleId = getVolunteerRoleId(volunteer);
			const roleName = getVolunteerRoleName(volunteer);

			return (
				roleId === ROLE_ID_HELFER ||
				roleId === ROLE_ID_SUPERHELFER ||
				roleName === ROLE_NAME_HELFER ||
				roleName === ROLE_NAME_SUPERHELFER
			);
		});
	}, [visibleVolunteers]);

	const okVolunteers = useMemo(() => {
		return visibleVolunteers.filter((volunteer) => {
			const roleId = getVolunteerRoleId(volunteer);
			const roleName = getVolunteerRoleName(volunteer);

			return roleId === ROLE_ID_OK || roleName === ROLE_NAME_OK;
		});
	}, [visibleVolunteers]);

	const enrichedShifts = useMemo(() => {
		return enrichShiftsWithVolunteerNames(shifts, volunteers);
	}, [shifts, volunteers]);

	const shiftSections = useMemo(() => {
		return buildAdminShiftSections(enrichedShifts);
	}, [enrichedShifts]);

	return (
		<div className="tabs-container">
			<div className="tab-bar">
				{tabs.map((tab) => (
					<button
						key={tab.id}
						className={
							"tab-button" +
							(activeTab === tab.id ? " tab-button-active" : "")
						}
						onClick={() => setActiveTab(tab.id)}
						type="button"
					>
						{tab.label}
					</button>
				))}
			</div>

			<div className="tab-panels">
				{activeTab === "overview" && (
					<div className="admin-mobile-panel">
						<h3 className="admin-subheading">Admin Übersicht</h3>
						<p>
							Hier sieht der Admin alle registrierten Volunteers,
							alle Schichten und alle Gäste.
						</p>
					</div>
				)}

				{activeTab === "helpers" && (
					<>
						<h3 className="admin-subheading">Helfer und Superhelfer</h3>

						<VolunteerList
							volunteers={helperVolunteers}
							setVolunteers={setVolunteers}
							reloadVolunteers={reloadVolunteers}
						/>

						<h3 className="admin-subheading">OK-Mitglieder</h3>

						<VolunteerList
							volunteers={okVolunteers}
							setVolunteers={setVolunteers}
							reloadVolunteers={reloadVolunteers}
						/>
					</>
				)}

				{activeTab === "shifts" && (
					<>
						<h3 className="admin-subheading">
							Alle Schichten aller Ressorts
						</h3>

						{shiftSections.length > 0 ? (
							shiftSections.map((section) => (
								<section
									key={section.key}
									className="admin-shift-section"
								>
									<h3 className="admin-subheading">
										{section.label}
									</h3>

									<ShiftTable
										shifts={section.shifts}
										departmentName={section.departmentName}
										autoRefresh={false}
									/>
								</section>
							))
						) : (
							<ShiftTable
								shifts={[]}
								autoRefresh={false}
							/>
						)}
					</>
				)}

				{activeTab === "guests" && (
					<>
						<GuestRegistration refreshGuestList={reloadGuests} />

						<GuestList
							guests={guests}
							onEdit={(guest) => setGuestToEdit(guest)}
							onDelete={(guest) => setGuestToDelete(guest)}
						/>
					</>
				)}
			</div>

			{selectedVolunteer && (
				<EditVolunteerModal
					volunteer={selectedVolunteer}
					onClose={() => setSelectedVolunteer(null)}
					onSaved={() => {
						setSelectedVolunteer(null);
						reloadVolunteers();
					}}
				/>
			)}

			{guestToEdit && (
				<EditGuestModal
					guest={guestToEdit}
					onClose={() => setGuestToEdit(null)}
					onSaved={() => {
						setGuestToEdit(null);
						reloadGuests();
					}}
				/>
			)}

			{guestToDelete && (
				<DeleteGuestModal
					guest={guestToDelete}
					onClose={() => setGuestToDelete(null)}
					onDeleted={() => {
						setGuestToDelete(null);
						reloadGuests();
					}}
				/>
			)}
		</div>
	);
};

export default AdminMobile;